import Button from 'components/Form/Button';
import SearchInput from 'components/Form/SearchInput';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getPaginationRowModel
} from '@tanstack/react-table';
import Paginator from 'components/TableWidget/Paginator';
import clsx from 'clsx';
import { useGetStaffList } from 'common/queries-and-mutations/admin/staff';
import { Link, useSearchParams } from 'react-router-dom';
import useDebounce from 'common/hooks/useDebounce';
import { StaffType } from 'service/dto/staff.dto';
import { ListOrder, PaginatedListMeta } from 'types/general.type';
import Badge from 'components/Badge';
import withCreatePortal from 'components/HOC/withCreatePortal';
import CreateStaffModal from './CreateStaffModal';
import SuccessModal from 'components/Modal/SuccessModal';
import { Loader } from 'components/Loader';

const EnhancedCreateStaffModal = withCreatePortal(CreateStaffModal);
const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function Staff() {
  const [urlParams, setUrlParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const searchQueryString = urlParams.get('search_query') ?? '';
  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const debounceSearch = useDebounce(searchQueryString, 1300);

  const getPaginationParams = useCallback(() => {
    return {
      page: +currentPage,
      take: +numberOfItemsPerPage,
      order: ListOrder.ASC
    };
  }, [currentPage, numberOfItemsPerPage]);

  const computedWhereOptions = useCallback(() => {
    if (searchQueryString.trim().length > 2) {
      return {
        search_query: searchQueryString
      };
    }
    return {
      search_query: searchQueryString.trim().length > 3 ? searchQueryString : ''
    };
  }, [searchQueryString]);

  const {
    data: staffList,
    isFetching,
    refetch
  } = useGetStaffList({
    whereOptions: computedWhereOptions(),
    paginationOptions: getPaginationParams(),
    enabled: isExecutingSearch
  });

  useEffect(() => {
    if (!isFetching && isExecutingSearch) {
      setIsExecutingSearch(false);
    }
  }, [isExecutingSearch, isFetching]);

  useEffect(() => {
    if (debounceSearch.length > 3) {
      setIsExecutingSearch(true);
    }
  }, [debounceSearch.length]);

  const columnHelper = createColumnHelper<StaffType>();

  const columns = [
    columnHelper.accessor(row => row.othername, {
      id: 'othername',
      cell: info => (
        <span className="text-xs font-normal">{`${info.row.original.othername} ${info.row.original.surname}`}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Name</span>
      )
    }),
    columnHelper.accessor(row => row.email, {
      id: 'email',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Email</span>
      )
    }),
    columnHelper.accessor(row => row.phoneNumber, {
      id: 'phoneNumber',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Phone Number
        </span>
      )
    }),
    columnHelper.accessor(row => row.totalCustomers, {
      id: 'totalCustomers',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Customers</span>
      )
    }),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      cell: info => (
        <span className="text-xs font-normal">
          {format(new Date(info.getValue()), 'dd MMM yyyy')}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Date Added</span>
      )
    }),
    columnHelper.accessor(row => row.isActive, {
      id: 'isActive',
      cell: info => (
        <Badge
          size="sm"
          title={info.getValue() === 1 ? 'Active' : 'Inactive'}
          type={info.getValue() === 1 ? 'success' : 'danger'}
          className="text-white"
        />
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Status</span>
      )
    }),
    columnHelper.accessor(row => row.id, {
      id: 'id',
      cell: info => (
        <div>
          <Link
            to={`details/${info.getValue()}`}
            className="rounded-md bg-primary-900 p-2 px-5 text-white underline"
          >
            View
          </Link>
        </div>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Action</span>
      )
    })
  ];

  const data = useMemo<StaffType[]>(() => {
    const normalizedResult: StaffType[] = (staffList?.data ?? []).map(item => ({
      id: item.id,
      email: item.email,
      isActive: item.isActive,
      totalCustomers: item.totalCustomers,
      surname: item.surname,
      othername: item.othername,
      phoneNumber: item.phoneNumber,
      accountType: item.accountType,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
      customers: item.customers
    }));
    return normalizedResult;
  }, [staffList]);

  const listMetaData = (staffList?.meta ?? {}) as PaginatedListMeta;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (value.trim() === '') {
      urlParams.delete(name);
      setUrlParams(urlParams);
    }
    if (value.trim()) {
      urlParams.set(name, value);
      setUrlParams(urlParams);
    }
  };

  const goToNextPage = () => {
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="my-4 flex items-center justify-between">
        <Breadcrumb title="Staff" />
        <div className="flex items-end justify-end gap-8">
          <Button
            variant="primary"
            size="md"
            className="w-[9rem] rounded-[3px] p-[0.1rem]"
            label="Create Staff"
            type="button"
            onClick={() => setShowAddModal(true)}
          ></Button>
        </div>
      </div>

      <div className="flex w-[24.3rem] items-center gap-x-5">
        <SearchInput
          name="search_query"
          placeholder="Search"
          value={searchQueryString}
          onChange={handleChange}
          size="lg"
          className="h-12 w-[24.3rem] rounded-[40px] border border-[#E9E9EA] bg-white"
        />
      </div>

      <div className="mt-3 flex flex-col overflow-auto rounded-[5px] bg-white">
        <table
          className={clsx({
            'w-full min-w-full': true,
            'flexs flex-col h-[30rem]': table.getRowModel().rows.length === 0
          })}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="border-b  text-xs text-[#1F4173]"
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="p-5 text-left font-medium text-[#1F4173]"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none flex'
                            : '',
                          onClick: header.column.getToggleSortingHandler()
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          // asc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // ),
                          // desc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // )
                          asc: <></>,
                          desc: <></>
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr className="mt-10 pt-10 text-center text-xl font-bold">
                <td colSpan={7}>No staff found</td>
              </tr>
            )}
            {table.getRowModel().rows.map((row, cellIndex) => (
              <tr key={row.id} className="">
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={clsx({
                      'p-5 text-xs': true,
                      'bg-[#F9F9F9]': cellIndex % 2 !== 0
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-5 p-5">
          <Paginator
            numberOfPages={listMetaData.pageCount}
            page={+currentPage}
            hasNext={listMetaData.hasNextPage}
            hasPrevious={listMetaData.hasPreviousPage}
            numberOfItemsPerPage={+numberOfItemsPerPage}
            setNumberOfItemsPerPage={num => {
              // setPageSize(num);
              urlParams.set('items_per_page', num.toString());
              setUrlParams(urlParams);
            }}
            goToNextPage={goToNextPage}
            goToPreviousPage={goToPreviousPage}
          />
        </div>
      </div>

      {showAddModal && (
        <EnhancedCreateStaffModal
          onClose={() => {
            setShowAddModal(false);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setShowSuccess(true);
            refetch();
          }}
        />
      )}

      {showSuccess && (
        <EnhancedSuccessModal
          title="Staff has been created Successfully"
          onProccessClick={() => {
            setShowSuccess(false);
          }}
          buttonText="Back to Users"
        />
      )}
    </div>
  );
}
